import { cn } from "./classNames-DkX30VJU.js";
import { X } from "./icons-DDR9dFSr.js";
import { Button } from "./Button-BgjVUBON.js";
import { ModalDescription, ModalOverlay, ModalTitle } from "./ModalTitle-7vgpfc6B.js";
import { cva } from "class-variance-authority";
import { jsx, jsxs } from "react/jsx-runtime";
import { Close, Content, Portal, Root, Title, Trigger } from "@radix-ui/react-dialog";
// src/components/Sheet/SheetBody.tsx
const SheetBody = ({ className, ...rest }) => /* @__PURE__ */ jsx("div", {
	...rest,
	className: cn("tw:flex", "tw:flex-col", "tw:overflow-x-hidden", "tw:px-4", className)
});
// src/components/Sheet/SheetOverlay.tsx
const SheetOverlay = ({ ref, ...props }) => /* @__PURE__ */ jsx(ModalOverlay, {
	ref,
	...props
});
// src/components/Sheet/SheetContent.tsx
const sheetContentVariants = cva([
	"tailwindcss-preflight",
	"tw:fixed",
	"tw:z-overlay",
	"tw:flex",
	"tw:flex-col",
	"tw:bg-card",
	"tw:text-card-foreground",
	"tw:shadow-lg",
	"tw:border",
	"tw:border-border",
	"tw:focus-visible:outline-hidden",
	"tw:focus-visible:ring-2",
	"tw:focus-visible:ring-ring/50",
	"tw:ease-in-out",
	"tw:data-[state=open]:duration-200",
	"tw:data-[state=closed]:duration-200"
], {
	defaultVariants: {
		side: "right",
		disableTransition: false
	},
	variants: {
		disableTransition: {
			true: [],
			false: ["tw:data-[state=open]:animate-in", "tw:data-[state=closed]:animate-out"]
		},
		side: {
			top: [
				"tw:inset-x-0",
				"tw:top-0",
				"tw:h-auto",
				"tw:data-[state=closed]:slide-out-to-top",
				"tw:data-[state=open]:slide-in-from-top",
				"tw:rounded-b-sheet"
			],
			right: [
				"tw:inset-y-0",
				"tw:right-0",
				"tw:h-full",
				"tw:data-[state=closed]:slide-out-to-right",
				"tw:data-[state=open]:slide-in-from-right",
				"tw:rounded-l-sheet"
			],
			bottom: [
				"tw:inset-x-0",
				"tw:bottom-0",
				"tw:h-auto",
				"tw:data-[state=closed]:slide-out-to-bottom",
				"tw:data-[state=open]:slide-in-from-bottom",
				"tw:drop-shadow-2xl",
				"tw:rounded-t-sheet"
			],
			left: [
				"tw:inset-y-0",
				"tw:left-0",
				"tw:h-full",
				"tw:data-[state=closed]:slide-out-to-left",
				"tw:data-[state=open]:slide-in-from-left",
				"tw:rounded-r-sheet"
			]
		}
	}
});
const SheetContent = ({ className, children, side = "right", disableTransition = false, hideOverlay = false, keepOpenOnPointerDownOutside = false, onPointerDownOutside, onInteractOutside, container, ...props }) => {
	const handleOnPointerDownOutside = (event) => {
		if (keepOpenOnPointerDownOutside) event.preventDefault();
		onPointerDownOutside?.(event);
	};
	const handleOnInteractOutside = (event) => {
		if (keepOpenOnPointerDownOutside) event.preventDefault();
		onInteractOutside?.(event);
	};
	return /* @__PURE__ */ jsxs(Portal, {
		container,
		children: [!hideOverlay && /* @__PURE__ */ jsx(SheetOverlay, { disableTransition: disableTransition ?? false }), /* @__PURE__ */ jsx(Content, {
			...props,
			"aria-describedby": void 0,
			className: cn(sheetContentVariants({
				side,
				className,
				disableTransition
			})),
			onPointerDownOutside: handleOnPointerDownOutside,
			onInteractOutside: handleOnInteractOutside,
			"data-side": side,
			children
		})]
	});
};
// src/components/Sheet/SheetFooter.tsx
const SheetFooter = ({ className, ...props }) => /* @__PURE__ */ jsx("div", {
	className: cn("tw:mt-auto tw:flex tw:flex-col tw:gap-2 tw:p-4", className),
	...props
});
// src/components/Sheet/SheetHeader.tsx
const SheetHeader = ({ className, children, hideCloseIcon = false, title, description, ...props }) => {
	const isHeaderRendered = title || description || !hideCloseIcon;
	return /* @__PURE__ */ jsxs("div", {
		className: cn("tw:flex", "tw:flex-col", "tw:gap-1.5", "tw:p-4", className),
		...props,
		children: [isHeaderRendered && /* @__PURE__ */ jsxs("div", {
			className: "tw:flex tw:flex-col",
			children: [/* @__PURE__ */ jsxs("div", {
				className: "tw:flex tw:justify-end tw:gap-2",
				children: [title && /* @__PURE__ */ jsx(ModalTitle, { children: title }), !hideCloseIcon && /* @__PURE__ */ jsx(Close, {
					asChild: true,
					children: /* @__PURE__ */ jsxs(Button, {
						variant: "invisible",
						size: "icon-sm",
						children: [/* @__PURE__ */ jsx(X, {}), /* @__PURE__ */ jsx("span", {
							className: "tw:sr-only",
							children: "Close"
						})]
					})
				})]
			}), description && /* @__PURE__ */ jsx(ModalDescription, { children: description })]
		}), children]
	});
};
// src/components/Sheet/SheetTitle.tsx
const SheetTitle = ({ className, visuallyHidden = false, ...props }) => /* @__PURE__ */ jsx(Title, {
	className: cn({ "tw:sr-only": visuallyHidden }, className),
	...props
});
// src/components/Sheet/Sheet.tsx
const Sheet = (props) => /* @__PURE__ */ jsx(Root, { ...props });
Sheet.Close = Close;
Sheet.Trigger = Trigger;
Sheet.Content = SheetContent;
Sheet.Title = SheetTitle;
Sheet.Header = SheetHeader;
Sheet.Footer = SheetFooter;
Sheet.Body = SheetBody;
export { Sheet };

//# sourceMappingURL=Sheet-DGqQqtof.js.map